import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAllMemberRoles, useRevokeMemberAccess, useSetMemberRoles, useUpdateMember } from "../api/members-api";
import { queryClient } from "../api/queryClient";
import { useLogeMembers } from "../api/user-api";
import { Icon } from "../components/dss/Icon";
import { FadeIn, useEnterAnimation } from "../components/dss/Motion";
import { AdminHeader, Field } from "../components/dss/Primitives";
import { Skeleton } from "../components/dss/Skeleton";
import { Role } from "../types/entities";
import { EMOJI_ONLY_ERROR, isEmojiOnly } from "../emoji";

const ROLE_OPTIONS: { key: Role; label: string; desc: string }[] = [
  { key: "ADMIN", label: "Admin", desc: "Opret og redigér samlinger, datoer og roller" },
  { key: "TAX_COLLECTOR", label: "Bødeminister", desc: "Tilføj, redigér og afskriv bøder" },
];

const EditMember = () => {
  const { memberId } = useParams();
  const id = Number(memberId);
  const { data: members } = useLogeMembers();
  const { data: roleRows = [] } = useAllMemberRoles();
  const { mutateAsync: updateMember, isLoading: isSaving } = useUpdateMember();
  const { mutateAsync: setRoles } = useSetMemberRoles();
  const { mutateAsync: revokeAccess } = useRevokeMemberAccess();
  const navigate = useNavigate();

  const member = members?.find((m) => m.id === id);
  const animateIn = useEnterAnimation(!member);
  const [name, setName] = useState("");
  const [emoji, setEmoji] = useState("");
  const [roles, setLocalRoles] = useState<Role[]>([]);
  const [emojiError, setEmojiError] = useState(false);
  const emojiInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (member) {
      setName(member.name);
      setEmoji(member.emoji ?? "");
    }
    setLocalRoles(roleRows.filter((r) => r.loge_member_id === id).map((r) => r.role));
  }, [member, roleRows, id]);

  const toggleRole = (role: Role) => {
    setLocalRoles((prev) => (prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role]));
  };

  const onSave = async () => {
    if (!isEmojiOnly(emoji)) {
      setEmojiError(true);
      emojiInputRef.current?.focus();
      return;
    }
    setEmojiError(false);
    await updateMember({ memberId: id, name, emoji });
    await setRoles({ memberId: id, roles });
    queryClient.invalidateQueries(["members"]);
    queryClient.invalidateQueries(["member_roles"]);
    navigate("/admin/members");
  };

  const onRevoke = async () => {
    await revokeAccess(id);
    queryClient.invalidateQueries(["members"]);
    navigate("/admin/members");
  };

  return (
    <div className="mx-auto max-w-md bg-paper pt-safe min-h-app">
      <AdminHeader
        back="Medlemmer"
        onBack={() => navigate("/admin/members")}
        title={member?.name ?? ""}
        sub="REDIGÉR MEDLEM"
        action={
          <button
            onClick={onSave}
            disabled={isSaving || !member}
            className="font-mono text-[10.5px] tracking-[1.6px] uppercase text-sage font-semibold flex items-center gap-1.5"
          >
            <Icon name="circle" size={5} />
            <span>Gem</span>
            <Icon name="arrow-right" size={13} />
          </button>
        }
      />

      {!member ? (
        <div className="px-7 pt-5 flex flex-col gap-5 pb-8">
          <div>
            <Skeleton className="w-24 h-3 mb-2.5" />
            <Skeleton className="w-full h-[46px] rounded-lg" />
          </div>
          <div>
            <Skeleton className="w-14 h-3 mb-2.5" />
            <Skeleton className="w-full h-[46px] rounded-lg" />
          </div>
          <div>
            <Skeleton className="w-14 h-3 mb-2.5" />
            <div className="flex flex-col gap-2">
              <Skeleton className="w-full h-[58px] rounded-lg" />
              <Skeleton className="w-full h-[58px] rounded-lg" />
              <Skeleton className="w-full h-[58px] rounded-lg" />
            </div>
          </div>
          <Skeleton className="w-full h-[52px] rounded-lg" />
        </div>
      ) : (
        <FadeIn enabled={animateIn} className="px-7 pt-5 flex flex-col gap-5 pb-8">
          <Field label="Navn i logen">
            <div
              className={`bg-card border rounded-lg py-3 px-3.5 flex items-center gap-2.5 ${
                emojiError ? "border-clay" : "border-line-soft"
              }`}
            >
              <input
                ref={emojiInputRef}
                value={emoji}
                onChange={(e) => {
                  setEmoji(e.target.value);
                  setEmojiError(false);
                }}
                // Counts UTF-16 units, not emoji — ZWJ sequences (families,
                // profession/gender combos) run well past 4 units each.
                maxLength={16}
                className="w-8 bg-transparent text-base text-center focus:outline-none"
              />
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="flex-1 bg-transparent font-display text-base font-bold uppercase tracking-tight focus:outline-none"
              />
            </div>
            {emojiError && (
              <div className="font-mono text-[9.5px] text-clay mt-1.5">{EMOJI_ONLY_ERROR}</div>
            )}
          </Field>

          <Field label="E-mail" hint="Bruges til engangskoden ved login">
            <div className="bg-card border border-line-soft rounded-lg py-3 px-3.5 font-mono text-sm">
              {member.email ?? "— afventer login —"}
            </div>
          </Field>

          <div>
            <div className="font-mono text-[10.5px] tracking-[1.6px] uppercase text-ink3 mb-2.5">[ Roller ]</div>
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-3 py-3.5 px-3.5 rounded-lg bg-card border border-line-soft opacity-70">
                <Icon name="user" variant="regular" size={14} className="w-[18px] text-ink3" />
                <div className="flex-1">
                  <div className="font-display text-sm font-bold uppercase tracking-tight">Medlem</div>
                  <div className="text-[11.5px] text-ink3 mt-0.5">Stem, se kalender og hovedbog</div>
                </div>
                <div className="w-[42px] h-6 rounded-full bg-ink3 flex items-center px-0.5 justify-end">
                  <div className="w-[18px] h-[18px] rounded-full bg-paper" />
                </div>
              </div>
              {ROLE_OPTIONS.map((r) => {
                const on = roles.includes(r.key);
                return (
                  <button
                    key={r.key}
                    onClick={() => toggleRole(r.key)}
                    className={`flex items-center gap-3 py-3.5 px-3.5 rounded-lg border text-left ${
                      on ? "bg-card border-bilberry" : "bg-transparent border-line-soft"
                    }`}
                  >
                    <Icon
                      name={r.key === "ADMIN" ? "sliders" : "coins"}
                      size={14}
                      className={`w-[18px] ${on ? "text-bilberry" : "text-ink3"}`}
                    />
                    <div className="flex-1">
                      <div
                        className={`font-display text-sm font-bold uppercase tracking-tight ${on ? "" : "text-ink3"}`}
                      >
                        {r.label}
                      </div>
                      <div className="text-[11.5px] text-ink3 mt-0.5 leading-snug">{r.desc}</div>
                    </div>
                    <div
                      className={`w-[42px] h-6 rounded-full flex items-center px-0.5 ${
                        on ? "bg-bilberry justify-end" : "bg-paper2 justify-start border border-line"
                      }`}
                    >
                      <div className={`w-[18px] h-[18px] rounded-full ${on ? "bg-paper" : "bg-line"}`} />
                    </div>
                  </button>
                );
              })}
            </div>
            <div className="font-mono text-[9.5px] tracking-wide text-ink3 mt-2.5 uppercase">
              Én person kan have flere roller
            </div>
          </div>

          <button
            onClick={onRevoke}
            className="w-full py-3.5 rounded-lg border border-clay text-clay font-display text-[13px] font-bold uppercase tracking-wide flex items-center justify-center gap-2"
          >
            <Icon name="user-slash" size={13} />
            <span>Fjern adgang</span>
          </button>
        </FadeIn>
      )}
    </div>
  );
};

export default EditMember;
